import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Megaphone, Plus, Send, Clock, AlertTriangle, Check, X, Search,
  Users, Trash2, Eye, Filter, Globe, UserCheck, Building2, Shield,
  ChevronDown, ChevronUp, Loader2, BarChart3
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import api from "../../lib/api";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } };

const PRIORITY_COLORS = {
  low: { border: "border-slate-200", badge: "bg-slate-100 text-slate-700" },
  normal: { border: "border-blue-200", badge: "bg-blue-100 text-blue-700" },
  high: { border: "border-amber-200", badge: "bg-amber-100 text-amber-700" },
  critical: { border: "border-red-200", badge: "bg-red-100 text-red-700" },
};

const CATEGORY_BADGES = {
  general: "bg-slate-100 text-slate-700",
  compliance: "bg-emerald-100 text-emerald-700",
  training: "bg-violet-100 text-violet-700",
  system: "bg-orange-100 text-orange-700",
};

const AUDIENCE_OPTIONS = [
  { value: "all", label: "Everyone", desc: "All users in your organization", icon: Globe },
  { value: "departments", label: "Departments", desc: "Select specific departments", icon: Building2 },
  { value: "roles", label: "Roles / Groups", desc: "Target by user role", icon: Shield },
  { value: "specific_users", label: "Specific Users", desc: "Pick individual users", icon: UserCheck },
];

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admins" },
  { value: "tenant_admin", label: "Tenant Admins" },
  { value: "trainer", label: "Trainers" },
  { value: "manager", label: "Managers" },
  { value: "learner", label: "Learners" },
];

// ─── Audience Picker Component ───────────────────────────
const AudiencePicker = ({ form, setForm, departments, users, usersLoading }) => {
  const [userSearch, setUserSearch] = useState("");

  const toggleArrayItem = (key, val) => {
    setForm(p => {
      const arr = p[key] || [];
      return { ...p, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const filteredUsers = users.filter(u =>
    (u.full_name || "").toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(userSearch.toLowerCase())
  );

  const audienceCount = () => {
    if (form.target_audience === "all") return users.length;
    if (form.target_audience === "departments") return users.filter(u => (form.target_departments || []).includes(u.department)).length;
    if (form.target_audience === "roles") return users.filter(u => (form.target_roles || []).includes(u.role)).length;
    if (form.target_audience === "specific_users") return (form.target_user_ids || []).length;
    return 0;
  };

  return (
    <div className="space-y-3" data-testid="audience-picker">
      <Label className="text-sm font-semibold text-slate-700">Audience</Label>
      {/* Audience type selector */}
      <div className="grid grid-cols-2 gap-2">
        {AUDIENCE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setForm(p => ({ ...p, target_audience: opt.value, target_departments: [], target_roles: [], target_user_ids: [] }))}
            className={`p-2.5 rounded-lg border text-left transition-all ${
              form.target_audience === opt.value
                ? "border-primary border-2 bg-primary/5"
                : "border-slate-200 hover:border-primary/30"
            }`}
            data-testid={`audience-${opt.value}`}
          >
            <div className="flex items-center gap-2">
              <opt.icon className={`w-4 h-4 ${form.target_audience === opt.value ? "text-primary" : "text-slate-400"}`} />
              <span className="text-xs font-semibold">{opt.label}</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5 ml-6">{opt.desc}</p>
          </button>
        ))}
      </div>

      {/* Department selection */}
      {form.target_audience === "departments" && (
        <div className="space-y-2 p-3 rounded-lg border bg-slate-50/50">
          <Label className="text-xs text-slate-600">Select Departments</Label>
          <div className="flex flex-wrap gap-1.5">
            {departments.map(d => {
              const selected = (form.target_departments || []).includes(d.name);
              return (
                <button
                  key={d.name}
                  type="button"
                  onClick={() => toggleArrayItem("target_departments", d.name)}
                  className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    selected
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"
                  }`}
                  data-testid={`dept-chip-${d.name}`}
                >
                  {selected && <Check className="w-3 h-3 inline mr-1" />}
                  {d.name}
                </button>
              );
            })}
            {departments.length === 0 && <p className="text-xs text-muted-foreground">No departments configured</p>}
          </div>
        </div>
      )}

      {/* Role selection */}
      {form.target_audience === "roles" && (
        <div className="space-y-2 p-3 rounded-lg border bg-slate-50/50">
          <Label className="text-xs text-slate-600">Select Roles</Label>
          <div className="flex flex-wrap gap-1.5">
            {ROLE_OPTIONS.map(r => {
              const selected = (form.target_roles || []).includes(r.value);
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => toggleArrayItem("target_roles", r.value)}
                  className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    selected
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"
                  }`}
                  data-testid={`role-chip-${r.value}`}
                >
                  {selected && <Check className="w-3 h-3 inline mr-1" />}
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* User selection */}
      {form.target_audience === "specific_users" && (
        <div className="space-y-2 p-3 rounded-lg border bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              placeholder="Search users by name or email..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
              data-testid="user-search-input"
            />
          </div>
          {(form.target_user_ids || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(form.target_user_ids || []).map(uid => {
                const u = users.find(x => x.id === uid);
                return (
                  <Badge key={uid} className="bg-primary/10 text-primary border-primary/20 text-xs gap-1 cursor-pointer hover:bg-primary/20" onClick={() => toggleArrayItem("target_user_ids", uid)}>
                    {u?.full_name || uid.slice(0, 8)}
                    <X className="w-2.5 h-2.5" />
                  </Badge>
                );
              })}
            </div>
          )}
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {usersLoading ? (
              <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>
            ) : filteredUsers.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-3">No users found</p>
            ) : filteredUsers.slice(0, 50).map(u => {
              const selected = (form.target_user_ids || []).includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleArrayItem("target_user_ids", u.id)}
                  className={`w-full flex items-center gap-2.5 p-2 rounded-md text-left transition-colors ${
                    selected ? "bg-primary/10" : "hover:bg-slate-100"
                  }`}
                  data-testid={`user-option-${u.id}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    selected ? "bg-primary text-white" : "bg-slate-200 text-slate-600"
                  }`}>
                    {selected ? <Check className="w-3 h-3" /> : (u.full_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{u.full_name || "Unknown"}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{u.email} · {u.department || "No dept"}</p>
                  </div>
                  {selected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center gap-2 p-2 rounded-md bg-blue-50/70 border border-blue-100">
        <Users className="w-3.5 h-3.5 text-blue-600" />
        <span className="text-xs text-blue-700 font-medium">
          {form.target_audience === "all"
            ? `All ${audienceCount()} users will receive this announcement`
            : form.target_audience === "departments"
            ? `${(form.target_departments || []).length} department(s) selected — ~${audienceCount()} users`
            : form.target_audience === "roles"
            ? `${(form.target_roles || []).length} role(s) selected — ~${audienceCount()} users`
            : `${(form.target_user_ids || []).length} user(s) selected`}
        </span>
      </div>
    </div>
  );
};

// ─── Audience display badge for announcement cards ───────
const AudienceBadge = ({ ann }) => {
  const audience = ann.target_audience || "all";
  if (audience === "all") return <Badge variant="outline" className="text-[10px] gap-1"><Globe className="w-2.5 h-2.5" /> Everyone</Badge>;
  if (audience === "departments" && ann.target_departments?.length)
    return <Badge variant="outline" className="text-[10px] gap-1"><Building2 className="w-2.5 h-2.5" /> {ann.target_departments.join(", ")}</Badge>;
  if (audience === "roles" && ann.target_roles?.length)
    return <Badge variant="outline" className="text-[10px] gap-1"><Shield className="w-2.5 h-2.5" /> {ann.target_roles.map(r => r.replace("_", " ")).join(", ")}</Badge>;
  if (audience === "specific_users")
    return <Badge variant="outline" className="text-[10px] gap-1"><UserCheck className="w-2.5 h-2.5" /> {ann.target_user_ids?.length || 0} users</Badge>;
  return null;
};

// ─── Main Page ───────────────────────────────────────────
const AnnouncementsPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [filterPriority, setFilterPriority] = useState("all");
  const [form, setForm] = useState({
    title: "", content: "", priority: "normal", category: "general",
    target_audience: "all", target_departments: [], target_roles: [], target_user_ids: [],
  });
  const [sending, setSending] = useState(false);
  const [previewAnn, setPreviewAnn] = useState(null);
  const [statsDrawer, setStatsDrawer] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSearch, setStatsSearch] = useState("");

  // Data for audience picker
  const [departments, setDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => { fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await api.get("/announcements?limit=50");
      setAnnouncements(res.data.announcements || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openStats = async (ann) => {
    setStatsDrawer(ann);
    setStatsLoading(true);
    setStatsSearch("");
    try {
      const res = await api.get(`/announcements/${ann.id}/stats`);
      setStatsData(res.data);
    } catch (e) { toast.error("Failed to load stats"); }
    finally { setStatsLoading(false); }
  };

  const loadAudienceData = async () => {
    setUsersLoading(true);
    try {
      const [dRes, uRes] = await Promise.all([
        api.get("/departments"),
        api.get("/users?limit=500"),
      ]);
      setDepartments(dRes.data || []);
      setAllUsers(uRes.data?.users || uRes.data || []);
    } catch (e) { console.error(e); }
    finally { setUsersLoading(false); }
  };

  const handleOpenCreate = () => {
    setForm({
      title: "", content: "", priority: "normal", category: "general",
      target_audience: "all", target_departments: [], target_roles: [], target_user_ids: [],
    });
    setShowCreate(true);
    loadAudienceData();
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.content.trim()) { toast.error("Content is required"); return; }
    if (form.target_audience === "departments" && !(form.target_departments || []).length) { toast.error("Select at least one department"); return; }
    if (form.target_audience === "roles" && !(form.target_roles || []).length) { toast.error("Select at least one role"); return; }
    if (form.target_audience === "specific_users" && !(form.target_user_ids || []).length) { toast.error("Select at least one user"); return; }
    setSending(true);
    try {
      const res = await api.post("/announcements", form);
      toast.success(`Announcement sent to ${res.data.notified_users} users`);
      setShowCreate(false);
      fetchAnnouncements();
    } catch (e) { toast.error("Failed to create announcement"); }
    finally { setSending(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Announcement deleted");
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch (e) { toast.error("Failed to delete"); }
  };

  const filtered = announcements.filter(a => {
    if (filterPriority !== "all" && a.priority !== filterPriority) return false;
    if (search && !a.title.toLowerCase().includes(search.toLowerCase()) && !a.content.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: announcements.length,
    active: announcements.filter(a => a.is_active).length,
    high: announcements.filter(a => a.priority === "high" || a.priority === "critical").length,
  };

  return (
    <AdminLayout title="Announcements" variant={variant}>
      <motion.div className="ent-page space-y-6" variants={container} initial="hidden" animate="show">
        {/* Header */}
        <motion.div variants={item} className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">Communications</p>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Organization Announcements</h1>
            <p className="text-sm text-slate-500 mt-1">Broadcast important updates to your team</p>
          </div>
          <Button className="ent-btn-primary gap-2" onClick={handleOpenCreate} data-testid="create-announcement-btn">
            <Plus className="w-4 h-4" /> New Announcement
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div variants={item} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: "Total Announcements", value: stats.total, icon: Megaphone, color: "#3b82f6" },
            { label: "Active", value: stats.active, icon: Check, color: "#10b981" },
            { label: "High Priority", value: stats.high, icon: AlertTriangle, color: "#f59e0b" },
          ].map((s, i) => (
            <div key={i} className="ent-stat" data-testid={`ann-stat-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="ent-icon-wrap" style={{ background: `${s.color}12` }}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} />
                </div>
              </div>
              <div className="stat-num text-slate-900">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Filters */}
        <motion.div variants={item} className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search announcements..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-xl border-slate-200" data-testid="ann-search" />
          </div>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[150px] rounded-xl border-slate-200" data-testid="ann-priority-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-slate-400" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Announcements List */}
        <motion.div variants={item} className="space-y-3">
          {filtered.map((ann) => {
            const p = PRIORITY_COLORS[ann.priority] || PRIORITY_COLORS.normal;
            const catBadge = CATEGORY_BADGES[ann.category] || CATEGORY_BADGES.general;
            return (
              <motion.div key={ann.id} variants={item}
                className={`ent-card p-5 border-l-4 ${p.border} hover:shadow-md transition-all cursor-pointer`}
                data-testid={`announcement-${ann.id}`}
                onClick={() => openStats(ann)}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <h3 className="text-base font-bold text-slate-900">{ann.title}</h3>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.badge}`}>{ann.priority}</span>
                      <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${catBadge}`}>{ann.category}</span>
                      <AudienceBadge ann={ann} />
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{ann.content}</p>
                    <div className="flex items-center gap-4 mt-2.5 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {ann.author_name}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(ann.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                    <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => openStats(ann)} data-testid={`ann-stats-${ann.id}`}>
                      <BarChart3 className="w-3 h-3 mr-1" /> Stats
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreviewAnn(ann)} data-testid={`ann-view-${ann.id}`}>
                      <Eye className="w-4 h-4 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(ann.id)} data-testid={`ann-delete-${ann.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div className="text-center py-16">
              <Megaphone className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No announcements yet</p>
              <p className="text-sm text-slate-400">Create your first announcement to communicate with your team</p>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Megaphone className="w-4 h-4 text-blue-600" /></div>
              New Announcement
            </DialogTitle>
            <DialogDescription>Create and broadcast a new announcement. Choose who receives it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1 block">Title</Label>
              <Input placeholder="e.g. Q1 Safety Training Deadline" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="ann-form-title" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1 block">Content</Label>
              <Textarea placeholder="Write your announcement..." rows={4} value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} data-testid="ann-form-content" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1 block">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="ann-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1 block">Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="ann-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Audience Picker */}
            <AudiencePicker
              form={form}
              setForm={setForm}
              departments={departments}
              users={allUsers}
              usersLoading={usersLoading}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="ent-btn-primary gap-2" onClick={handleCreate} disabled={sending} data-testid="ann-form-submit">
              <Send className="w-4 h-4" /> {sending ? "Sending..." : "Send Announcement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewAnn} onOpenChange={() => setPreviewAnn(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">{previewAnn?.title}</DialogTitle>
            <DialogDescription>Announcement details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full ${PRIORITY_COLORS[previewAnn?.priority]?.badge || ""}`}>{previewAnn?.priority}</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_BADGES[previewAnn?.category] || ""}`}>{previewAnn?.category}</span>
              {previewAnn && <AudienceBadge ann={previewAnn} />}
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{previewAnn?.content}</p>
            <div className="flex items-center gap-4 pt-2 text-xs text-slate-400 border-t border-slate-100">
              <span>By {previewAnn?.author_name}</span>
              <span>{previewAnn?.created_at && new Date(previewAnn.created_at).toLocaleString()}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Announcement Stats Side Drawer */}
      <Sheet open={!!statsDrawer} onOpenChange={(v) => { if (!v) { setStatsDrawer(null); setStatsData(null); setStatsSearch(""); } }}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
          {statsDrawer && (
            <>
              <div className="p-5 border-b">
                <SheetHeader className="mb-0">
                  <SheetTitle className="text-lg">{statsDrawer.title}</SheetTitle>
                  <SheetDescription className="text-xs">{statsDrawer.category} · {new Date(statsDrawer.created_at).toLocaleDateString()}</SheetDescription>
                </SheetHeader>
              </div>
              {statsLoading ? (
                <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
              ) : statsData ? (
                <div className="flex-1 overflow-y-auto">
                  {/* Stats Summary */}
                  <div className="grid grid-cols-3 gap-3 p-5 border-b">
                    <div className="text-center p-3 rounded-xl bg-emerald-50">
                      <p className="text-xl font-bold text-emerald-700">{statsData.viewed_count}</p>
                      <p className="text-[10px] text-emerald-600 font-medium">Viewed</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-xl font-bold text-slate-700">{statsData.not_viewed_count}</p>
                      <p className="text-[10px] text-slate-600 font-medium">Not Viewed</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-blue-50">
                      <p className="text-xl font-bold text-blue-700">{statsData.view_rate}%</p>
                      <p className="text-[10px] text-blue-600 font-medium">View Rate</p>
                    </div>
                  </div>

                  {/* View Progress Bar */}
                  <div className="px-5 py-3 border-b">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>{statsData.viewed_count} of {statsData.total_audience} viewed</span>
                      <span>{statsData.view_rate}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${statsData.view_rate}%` }} />
                    </div>
                  </div>

                  {/* Viewed Users List */}
                  <div className="p-5">
                    <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
                      <Eye className="w-4 h-4 text-primary" /> Users Who Viewed ({statsData.viewed_users?.length || 0})
                    </h3>
                    {(statsData.viewed_users?.length || 0) > 3 && (
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input placeholder="Search viewers..." value={statsSearch} onChange={e => setStatsSearch(e.target.value)} className="pl-10 h-8 text-xs" data-testid="stats-search" />
                      </div>
                    )}
                    {(statsData.viewed_users || []).filter(u =>
                      !statsSearch || u.full_name?.toLowerCase().includes(statsSearch.toLowerCase()) || u.email?.toLowerCase().includes(statsSearch.toLowerCase())
                    ).length === 0 ? (
                      <div className="text-center py-6">
                        <Eye className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-xs text-muted-foreground">{(statsData.viewed_users?.length || 0) === 0 ? "No views yet" : "No viewers match search"}</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {(statsData.viewed_users || []).filter(u =>
                          !statsSearch || u.full_name?.toLowerCase().includes(statsSearch.toLowerCase()) || u.email?.toLowerCase().includes(statsSearch.toLowerCase())
                        ).map((u, i) => (
                          <div key={u.user_id} className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-muted/30 transition-colors" data-testid={`stats-viewer-${i}`}>
                            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-xs font-bold text-emerald-700 shrink-0">
                              {u.full_name?.charAt(0) || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{u.full_name}</p>
                              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                <span>{u.email}</span>
                                {u.department && <span className="bg-muted px-1.5 py-0.5 rounded">{u.department}</span>}
                              </div>
                            </div>
                            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">Viewed</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
};

export default AnnouncementsPage;
